import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/core/stores/auth'
import { AppLayout } from '@/core/layouts/AppLayout'
import { LoginPage } from '@/core/components/LoginPage'
import { SignupPage } from '@/core/components/SignupPage'
import { AuthCallback } from '@/core/components/AuthCallback'
import { TeamPage } from '@/core/components/TeamPage'
import { TremorWikiPage } from '@/core/pages/TremorWikiPage'
import { HomePage } from '@/core/pages/HomePage'
import { WikiArticlePage } from '@/core/pages/wiki/WikiArticlePage'
import { VerifyEmailPage } from '@/core/pages/VerifyEmailPage'
import { InviteAcceptPage } from '@/core/pages/InviteAcceptPage'
import { CGUPage } from '@/core/pages/CGUPage'
import { CGVPage } from '@/core/pages/CGVPage'
import { ConnectorsPage } from '@/core/pages/ConnectorsPage'
import { OnboardingPage } from '@/core/pages/OnboardingPage'
import { OrganizationSettingsPage } from '@/core/pages/OrganizationSettingsPage'
import { SetupOrganizationPage } from '@/core/pages/SetupOrganizationPage'

// LLR pages (core module)
import { RadarHubPage } from '@/modules/local-lead-radar/pages/RadarHubPage'
import { AlertCenterPage } from '@/modules/local-lead-radar/pages/AlertCenterPage'
import { RadarSetupPage } from '@/modules/local-lead-radar/pages/RadarSetupPage'
// GFO pages (add-on)
import { DashboardPage } from '@/modules/google-footprint/pages/DashboardPage'
import { KeywordsPage } from '@/modules/google-footprint/pages/KeywordsPage'
import { ReviewsPage } from '@/modules/google-footprint/pages/ReviewsPage'
import { OpportunitiesPage } from '@/modules/google-footprint/pages/OpportunitiesPage'
import { SettingsPage } from '@/modules/google-footprint/pages/SettingsPage'
// ARE pages (add-on)
import { AREDashboardPage } from '@/modules/auto-reputation-engine/pages/AREDashboardPage'
import { ContactHubPage } from '@/modules/auto-reputation-engine/pages/ContactHubPage'
import { ActivityFeedPage } from '@/modules/auto-reputation-engine/pages/ActivityFeedPage'
import { FeedbackInboxPage } from '@/modules/auto-reputation-engine/pages/FeedbackInboxPage'
import { CampaignSetupPage } from '@/modules/auto-reputation-engine/pages/CampaignSetupPage'
import { AnalyticsPage } from '@/modules/auto-reputation-engine/pages/AnalyticsPage'
// SDI pages (add-on)
import { MarketsListPage } from '@/modules/search-demand-intelligence/pages/MarketsListPage'
import { MarketOverviewPage } from '@/modules/search-demand-intelligence/pages/MarketOverviewPage'
import { KeywordClusterPage } from '@/modules/search-demand-intelligence/pages/KeywordClusterPage'
import { BudgetSimulatorPage } from '@/modules/search-demand-intelligence/pages/BudgetSimulatorPage'
import { OpportunityMapPage } from '@/modules/search-demand-intelligence/pages/OpportunityMapPage'
import { SetupPage } from '@/modules/search-demand-intelligence/pages/SetupPage'
import { SDIDashboardPage } from '@/modules/search-demand-intelligence/pages/SDIDashboardPage'


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user, validateSession } = useAuthStore()
  const [checked, setChecked] = useState(false)
  
  useEffect(() => {
    validateSession().finally(() => setChecked(true))
  }, [])

  if (!checked || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="animate-spin w-8 h-8 border-4 border-ink-border border-t-gold rounded-full" />
      </div>
    )
  }
  

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (user && !user.onboarding_completed) return <Navigate to="/setup" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <Routes>
      {/* Public routes — no auth required */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/cgu" element={<CGUPage />} />
      <Route path="/cgv" element={<CGVPage />} />
      <Route path="/onboarding/:token" element={<OnboardingPage />} />
      <Route path="/settings/organization" element={<OrganizationSettingsPage />} />
      <Route path="/setup" element={<SetupOrganizationPage />} />

      {/* Protected routes — auth required */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/wiki" element={<TremorWikiPage />} />
                <Route path="/wiki/:articleId" element={<WikiArticlePage />} />
                {/* Connecteurs routes */}
                <Route path="/settings/connectors" element={<ConnectorsPage />} />
                {/* LLR routes */}
                <Route path="/llr" element={<RadarHubPage />} />
                <Route path="/llr/alerts" element={<AlertCenterPage />} />
                <Route path="/llr/radars/new" element={<RadarSetupPage />} />
                {/* GFO routes */}
                <Route path="/gfo" element={<DashboardPage />} />
                <Route path="/gfo/keywords" element={<KeywordsPage />} />
                <Route path="/gfo/reviews" element={<ReviewsPage />} />
                <Route path="/gfo/opportunities" element={<OpportunitiesPage />} />
                {/* ARE routes */}
                <Route path="/are" element={<AREDashboardPage />} />
                <Route path="/are/contacts" element={<ContactHubPage />} />
                <Route path="/are/activity" element={<ActivityFeedPage />} />
                <Route path="/are/feedback" element={<FeedbackInboxPage />} />
                <Route path="/are/campaigns/new" element={<CampaignSetupPage />} />
                <Route path="/are/analytics" element={<AnalyticsPage />} />
                {/* SDI routes */}
                <Route path="/sdi" element={<SDIDashboardPage />} />
                <Route path="/sdi/markets" element={<MarketsListPage />} />
                <Route path="/sdi/markets/:id" element={<MarketOverviewPage />} />
                <Route path="/sdi/markets/:id/keywords" element={<KeywordClusterPage />} />
                <Route path="/sdi/markets/:id/budget" element={<BudgetSimulatorPage />} />
                <Route path="/sdi/markets/:id/opportunities" element={<OpportunityMapPage />} />
                <Route path="/sdi/markets/:id/setup" element={<SetupPage />} />
                {/* Shared */}
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/team" element={<TeamPage />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
